import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { ClientSidebar } from "@/components/sidebars";
import { useAuth } from "@/lib/auth";
import { apiClient, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { getBrand } from "@/lib/brand";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return "—"; }
};

const fmtMoney = (amount, currency) => {
  if (amount == null) return "—";
  const cur = (currency || "MXN").toUpperCase();
  try { return new Intl.NumberFormat("es-MX", { style: "currency", currency: cur }).format(Number(amount)); }
  catch { return `${cur} ${amount}`; }
};

const statusPill = (s) => {
  const map = {
    paid: { bg: "#D1FAE5", fg: "#065F46", label: "Pagado" },
    initiated: { bg: "#FEF3C7", fg: "#92400E", label: "Iniciado" },
    failed: { bg: "#FEE2E2", fg: "#991B1B", label: "Fallido" },
    refunded: { bg: "#E0E7FF", fg: "#3730A3", label: "Reembolsado" },
  };
  const cfg = map[s] || { bg: "#E5E7EB", fg: "#374151", label: s || "—" };
  return (
    <span style={{ background: cfg.bg, color: cfg.fg, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
      {cfg.label}
    </span>
  );
};

export default function MiPlan() {
  const { user, refresh } = useAuth();
  const brand = getBrand();
  const PLANES = brand.planes;
  const cur = brand.currency;
  const sym = brand.currencySymbol || "$";
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payLoading, setPayLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);

  const isActive = user?.membership_status === "active";
  const renewalCancelled = !!user?.renewal_cancelled;
  const planNameFor = (id) => {
    if (!id) return "—";
    const found = PLANES.find((p) => p.id === id);
    return found?.nombre || id;
  };

  useEffect(() => {
    refresh();
    setPayLoading(true);
    apiClient.get("/me/payments")
      .then(({ data }) => setPayments(Array.isArray(data) ? data : []))
      .catch(() => setPayments([]))
      .finally(() => setPayLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const elegir = async (plan, term = "mensual") => {
    if (!user?.beneficiarios?.length) {
      toast.error("Primero agrega al menos un beneficiario.");
      return;
    }
    setLoadingPlan(plan.id + term);
    try {
      const { data } = await apiClient.post("/checkout/session", { plan_id: plan.id, origin_url: window.location.origin, term });
      const value = term === "msi12" ? parseFloat(plan.precio) * 12 : parseFloat(plan.precio);
      trackEvent("begin_checkout", {
        currency: cur,
        value,
        items: [{ item_id: plan.id, item_name: plan.nombre, price: value, quantity: 1 }],
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoadingPlan(null);
    }
  };

  const confirmarCancelar = async () => {
    setCancelBusy(true);
    try {
      await apiClient.post("/me/cancel-renewal", { motivo: cancelMotivo });
      toast.success("Renovación cancelada. Tu servicio sigue activo hasta la fecha de vencimiento.");
      setCancelOpen(false);
      setCancelMotivo("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCancelBusy(false);
    }
  };

  const reactivarRenovacion = async () => {
    try {
      await apiClient.post("/me/reactivate-renewal");
      toast.success("Renovación automática reactivada.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppLayout sidebar={ClientSidebar}>
      <header className="app-header">
        <h1>Mi Plan</h1>
        <p className="text-muted">
          Estado actual: <strong>{isActive ? `Activa (${user?.plan})` : "Pendiente"}</strong>
        </p>
      </header>

      {/* ===== Portal de autoservicio (solo si activa) ===== */}
      {isActive && (
        <section className="panel" data-testid="portal-autoservicio">
          <h2 style={{ margin: "0 0 6px", display: "flex", alignItems: "center", gap: 10 }}>
            <i className="fas fa-user-gear" style={{ color: "#163680" }} /> Administra tu membresía
          </h2>
          <div className="cards-row" style={{ marginTop: 14 }}>
            <div className="stat-card" data-testid="card-plan-actual">
              <span className="stat-label">Plan actual</span>
              <strong className="stat-value" style={{ fontSize: 18 }}>{planNameFor(user?.plan)}</strong>
              <small>Vence el {fmtDate(user?.membership_active_until)}</small>
            </div>
            <div
              className={`stat-card ${renewalCancelled ? "stat-warn" : "stat-ok"}`}
              data-testid="card-renovacion"
            >
              <span className="stat-label">Renovación</span>
              <strong className="stat-value" style={{ fontSize: 18 }}>
                {renewalCancelled ? "Cancelada" : "Automática"}
              </strong>
              {renewalCancelled ? (
                <button
                  className="stat-link"
                  onClick={reactivarRenovacion}
                  data-testid="btn-reactivar-renovacion"
                  style={{ background: "none", border: "none", color: "#163680", cursor: "pointer", padding: 0, fontWeight: 700 }}
                >
                  Reactivar renovación →
                </button>
              ) : (
                <button
                  className="stat-link"
                  onClick={() => setCancelOpen(true)}
                  data-testid="btn-abrir-cancelar"
                  style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", padding: 0, fontWeight: 700 }}
                >
                  Cancelar renovación →
                </button>
              )}
            </div>
            <div className="stat-card" data-testid="card-pagos-total">
              <span className="stat-label">Pagos registrados</span>
              <strong className="stat-value" style={{ fontSize: 18 }}>{payments.length}</strong>
              <small>Historial completo abajo</small>
            </div>
          </div>

          {cancelOpen && (
            <div
              data-testid="modal-cancelar-renovacion"
              style={{ marginTop: 16, padding: 16, borderRadius: 10, background: "#FEF2F2", border: "1px solid #FCA5A5" }}
            >
              <strong style={{ color: "#7F1D1D", display: "block", marginBottom: 8 }}>
                <i className="fas fa-triangle-exclamation" style={{ marginRight: 6 }} />
                ¿Cancelar la renovación automática?
              </strong>
              <p style={{ margin: "0 0 10px", color: "#991B1B", fontSize: 13 }}>
                Tu servicio se mantiene activo hasta el <strong>{fmtDate(user?.membership_active_until)}</strong>. Podrás reactivarlo antes de esa fecha.
              </p>
              <label style={{ display: "block", fontSize: 12, color: "#7F1D1D", marginBottom: 6, fontWeight: 600 }}>
                Cuéntanos por qué (opcional):
              </label>
              <textarea
                value={cancelMotivo}
                onChange={(e) => setCancelMotivo(e.target.value)}
                rows={3}
                data-testid="cancel-motivo-input"
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #FCA5A5", fontSize: 13, resize: "vertical" }}
                placeholder="Ej. Ya no lo necesito, es muy caro, encontré otra opción..."
              />
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button
                  onClick={confirmarCancelar}
                  disabled={cancelBusy}
                  className="btn"
                  data-testid="btn-confirmar-cancelar"
                  style={{ background: "#B91C1C", color: "white" }}
                >
                  {cancelBusy ? "Cancelando…" : "Confirmar cancelación"}
                </button>
                <button
                  onClick={() => { setCancelOpen(false); setCancelMotivo(""); }}
                  className="btn btn-secondary"
                  data-testid="btn-cerrar-cancelar"
                >
                  Regresar
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ===== Historial de pagos ===== */}
      {isActive && (
        <section className="panel" data-testid="historial-pagos">
          <h2 style={{ margin: "0 0 6px", display: "flex", alignItems: "center", gap: 10 }}>
            <i className="fas fa-receipt" style={{ color: "#7DC242" }} /> Historial de pagos
          </h2>
          {payLoading ? (
            <p className="text-muted">Cargando…</p>
          ) : payments.length === 0 ? (
            <p className="text-muted">Aún no tienes pagos registrados.</p>
          ) : (
            <table className="data-table" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Monto</th>
                  <th>Estatus</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.session_id || p.id} data-testid={`payment-row-${p.session_id || p.id}`}>
                    <td><small>{new Date(p.created_at).toLocaleString("es-MX")}</small></td>
                    <td>{planNameFor(p.plan_id)}{p.addons?.length ? ` · ${p.addons.map((a) => a.name || a.id).join(", ")}` : ""}</td>
                    <td>{fmtMoney(p.amount, p.currency)}</td>
                    <td>{statusPill(p.payment_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <div className="planes-grid">
        {PLANES.map((p) => (
          <div key={p.id} className={`plan-card ${p.featured ? "featured" : ""}`} data-testid={`plan-${p.id}`}>
            {p.featured && <div className="plan-badge">MÁS POPULAR</div>}
            <div className="plan-header"><h3>{p.nombre}</h3>{p.desc && <p className="text-muted" style={{ fontSize: 13, margin: "4px 0 0" }}>{p.desc}</p>}</div>
            {p.promoBadge && (
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <span style={{ display: "inline-block", background: "#E8F5D9", color: "#5BA628", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{p.promoBadge}</span>
              </div>
            )}
            <div className="plan-price"><span className="currency">{cur} {sym}</span><span className="amount">{p.precio}</span><span className="period">{p.periodo || "/mes"}</span></div>
            <div style={{ textAlign: "center", fontSize: 12, color: "#6b7280", marginTop: -6, marginBottom: 6 }}>Precio mensual <strong>+ IVA</strong></div>
            <ul className="plan-features">
              {p.features.map((f, i) => (
                <li key={i} style={{ opacity: f.ok === false ? 0.45 : 1 }}>
                  <i className={f.ok === false ? "fas fa-times" : "fas fa-check"} style={f.ok === false ? { color: "#cbd5e1" } : undefined} />
                  {typeof f === "string" ? f : f.text}
                </li>
              ))}
            </ul>
            <button onClick={() => elegir(p, "mensual")} disabled={loadingPlan === p.id + "mensual"} className="btn btn-primary plan-btn" data-testid={`plan-elegir-${p.id}`}>
              {loadingPlan === p.id + "mensual" ? "Procesando..." : user?.plan === p.id && isActive ? "Plan actual" : "Pagar mensual"}
            </button>
            <div style={{ marginTop: 10, padding: "10px 12px", background: "#F0F7FF", border: "1px dashed #163680", borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#163680" }}>
                <i className="fas fa-credit-card" style={{ marginRight: 6 }} />12 meses sin intereses
              </div>
              <div style={{ fontSize: 12, color: "#374151", margin: "4px 0 8px" }}>
                <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>{cur} {sym}{(Number(p.precio) * 12).toLocaleString("es-MX")}</span>{" → "}
                12 pagos de {cur} {sym}{(Number(p.precio) / 2).toLocaleString("es-MX")} = <strong>{cur} {sym}{(Number(p.precio) * 6).toLocaleString("es-MX")}</strong> en total <strong>+ IVA</strong>.
                <br /><span style={{ color: "#DC2626", fontWeight: 800, textTransform: "uppercase" }}>Págalo a meses con tu tarjeta de crédito, sin intereses y recibe el 50% de descuento</span>
              </div>
              <button onClick={() => elegir(p, "msi12")} disabled={loadingPlan === p.id + "msi12"} className="btn plan-btn" data-testid={`plan-msi12-${p.id}`}
                style={{ width: "100%", background: "#163680", color: "white" }}>
                {loadingPlan === p.id + "msi12" ? "Procesando..." : "Pagar a 12 MSI"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
